import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { OarTaskApi } from '../../apis/oar-task-api';
import type { OarTaskDataLoader } from '../oar-task-data-loader';
import type { OarTaskContent } from '../../types/oar-types';
import { OarParseStatus } from '../../types/oar-types';
import { mapOfficeActionHeader } from '../../mappers/oar-content-mapper';
import { OarTaskContentModel } from '../../models/oar-task-content-model';

/**
 * Header 数据加载器
 *
 * 负责加载审查意见通知书的头部信息，包括：
 * - 文档类型、机构、文档编号
 * - 申请号、发明名称、申请人
 * - 代理机构信息
 * - 条形码信息
 *
 * 工作模式：
 * - 当状态为 SUCCESS 时：调用 getOfficeActionHeader 获取解析结果
 * - 当状态为其他状态时：调用 parseOfficeActionHeader 触发解析
 */
@injectable()
export class HeaderDataLoader implements OarTaskDataLoader {
    readonly name = 'Header';

    constructor(
        @inject(OarTaskApi)
        private readonly _api: OarTaskApi,
        @inject(OarTaskContentModel)
        private readonly _model: OarTaskContentModel,
    ) {}

    /**
     * Header 部分总是需要加载
     */
    shouldLoad(state: OarTaskContent): boolean {
        return true;
    }

    /**
     * 加载 Header 数据
     *
     * 根据当前解析状态决定执行哪个操作：
     * - SUCCESS: 获取解析结果（调用 getOfficeActionHeader）
     * - 其他状态: 触发解析（调用 parseOfficeActionHeader）
     */
    async load(taskId: string): Promise<Partial<OarTaskContent>> {
        const currentState = this._model.modelData;
        const parseStatus = currentState.oarHeaderParseStatus;

        // 根据状态决定调用哪个 API
        let headerInfo;
        if (parseStatus === OarParseStatus.SUCCESS) {
            // 触发解析
            headerInfo = await this._api.getOfficeActionHeader({
                path: { task_id: taskId },
            });
        } else {
            // 获取已解析的结果
            headerInfo = await this._api.parseOfficeActionHeader({
                path: { task_id: taskId },
            });
        }

        // 使用映射器转换 API 响应到领域模型
        const mappedHeader = mapOfficeActionHeader(headerInfo);

        return {
            oarHeaderInfo: mappedHeader,
            oarPatentOffice: mappedHeader.authority,
        };
    }
}
